import React, { useEffect, useRef, useState } from 'react'
import { Store, ToastItem } from '../../utils/toast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import './ToastContainer.scss'

const iconMap = {
  success: faCheckCircle,
  danger: faTimesCircle,
  info: faInfoCircle,
  warning: faExclamationTriangle,
}

interface ToastItemState extends ToastItem {
  exiting: boolean
}

const SingleToast = ({
  item,
  onRemove,
}: {
  item: ToastItemState
  onRemove: (id: string) => void
}) => {
  const progressRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = progressRef.current
    if (!el) return
    el.style.transition = `width ${item.duration}ms linear`
    // kick off animation after paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.width = '0%'
      })
    })
  }, [item.duration])

  return (
    <div
      className={`sushi-toast sushi-toast--${item.type} ${item.exiting ? 'sushi-toast--exit' : 'sushi-toast--enter'}`}
    >
      <span className='sushi-toast__icon-wrap'>
        <FontAwesomeIcon icon={iconMap[item.type]} />
      </span>

      <div className='sushi-toast__body'>
        {item.title && <span className='sushi-toast__title'>{item.title}</span>}
        <span className='sushi-toast__message'>{item.message}</span>
      </div>

      <button
        className='sushi-toast__close'
        onClick={() => onRemove(item.id)}
        aria-label='close'
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className='sushi-toast__progress'>
        <div
          ref={progressRef}
          className='sushi-toast__progress-bar'
          style={{ width: '100%' }}
        />
      </div>
    </div>
  )
}

const ToastContainer = () => {
  const [toasts, setToasts] = useState<ToastItemState[]>([])

  useEffect(() => {
    const unsubAdd = Store._onAdd((item) => {
      setToasts((prev) => [...prev, { ...item, exiting: false }])

      setTimeout(() => {
        triggerExit(item.id)
      }, item.duration)
    })

    const unsubRemove = Store._onRemove((id) => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    })

    return () => {
      unsubAdd()
      unsubRemove()
    }
  }, [])

  function triggerExit(id: string) {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
    )
    setTimeout(() => {
      Store._remove(id)
    }, 350)
  }

  function handleRemove(id: string) {
    triggerExit(id)
  }

  return (
    <div className='sushi-toast-container'>
      {toasts.map((t) => (
        <SingleToast key={t.id} item={t} onRemove={handleRemove} />
      ))}
    </div>
  )
}

export default ToastContainer
